import React, {PropsWithChildren, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TimeSpentOnSlideDTO, TopSlidesAnalyticsEnum} from '../../../../incase-domain/proptypes/slidesAnalytics';
import {FormattedViewsAnalytics} from '../../../../incase-domain/analytics';
import {compareByValue} from '../../../util/util';
import {slideIndex} from '../../../../utils/slide';
import {IPresentation} from '../../../../incase-domain/presentation';
import SkeletonLoader from '../../../components/common/SkeletonLoader';
import Label from '../../../../incase-domain/components/label/Label';
import ReportProgressBar from '../../../../incase-domain/components/report/progressBar/ReportProgressBar';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Button from '../../../../incase-domain/components/button/Button';

interface MostViewedSlidesProps {
    selectedPresentation: IPresentation;
    mostViewedSlides: TimeSpentOnSlideDTO[];
    setSlideToViewID: (id: string) => void;
    isLoading: boolean;
}

function MostViewedSlides(props: PropsWithChildren<MostViewedSlidesProps>): JSX.Element {
    const {selectedPresentation, mostViewedSlides, setSlideToViewID, isLoading} = props;
    const {t} = useTranslation();

    const [topSlidesAnalytics, setTopSlidesAnalytics] = useState<TopSlidesAnalyticsEnum>(TopSlidesAnalyticsEnum.ALL);
    const [formattedData, setFormattedData] = useState<FormattedViewsAnalytics>({data: [], sum: 0});

    useEffect(() => {
        setFormattedData({sum: 0, data: []});
        let sum = 0;
        mostViewedSlides.forEach((slide, i) => {
            if (i < topSlidesAnalytics || topSlidesAnalytics === TopSlidesAnalyticsEnum.ALL) {
                const slideData = slideIndex(selectedPresentation, slide.id);
                sum += slide.value;
                setFormattedData((prev) => ({
                    ...prev,
                    data: [
                        ...prev.data,
                        {
                            id: slide.id,
                            name: slideData.name,
                            value: slide.value,
                        },
                    ],
                }));
            }
        });
        setFormattedData((prev) => ({
            sum,
            data: prev.data.sort(compareByValue),
        }));
    }, [selectedPresentation, mostViewedSlides, topSlidesAnalytics]);

    const maxViews = useMemo(() => {
        return formattedData.data.reduce((max, slide) => Math.max(max, slide.value), 0);
    }, [formattedData]);

    const topSlidesDropdownItems = [
        [
            {
                label: t('reportPage.all'),
                onClick: () => setTopSlidesAnalytics(TopSlidesAnalyticsEnum.ALL),
            },
            {
                label: `${t('reportPage.topViewedSlides')}: 5`,
                onClick: () => setTopSlidesAnalytics(TopSlidesAnalyticsEnum.FIVE),
            },
            {
                label: `${t('reportPage.topViewedSlides')}: 10`,
                onClick: () => setTopSlidesAnalytics(TopSlidesAnalyticsEnum.TEN),
            },
        ],
    ];

    return (
        <SkeletonLoader isLoading={isLoading} props={{style: {minHeight: '12rem'}}}>
            <div className="u-flex u-flex-col u-gap-3">
                <div className="u-flex u-h-between u-v-center">
                    <Label size="lg">{t('reportPage.mostViewedSlides.title')}</Label>
                    <DropdownMenuCompact menuPosition={DropdownMenuCompactMenuPosition.LEFT} items={topSlidesDropdownItems}>
                        <Button variant="ghost" size="sm" trailing="solar:alt-arrow-down-linear">{`${t('reportPage.topViewedSlides')}: ${
                            topSlidesAnalytics || t('reportPage.all')
                        }`}</Button>
                    </DropdownMenuCompact>
                </div>
                <div className="u-flex u-flex-col u-gap-2 u-ml-2">
                    {formattedData.data.length > 0 ? (
                        formattedData.data.map((slide) => (
                            <div onClick={() => setSlideToViewID(slide.id)}>
                                <ReportProgressBar
                                    label={slide.name}
                                    value={slide.value}
                                    maxValue={maxViews}
                                    score={t('reportPage.views', {count: slide.value})}
                                    hideValue
                                />
                            </div>
                        ))
                    ) : (
                        <Label size="md">{t('reportPage.noData')}</Label>
                    )}
                </div>
            </div>
        </SkeletonLoader>
    );
}

export default MostViewedSlides;
